import { useCallback } from "react";
import { useFileUploadContext } from "./file-uploads-context";
import { MultiFileUploadParams } from "./multi-file-upload-manager";
import { SingleFileUploadParams } from "./upload-manager";

/**
 * Type of the callback returned by the useMultiFileUploader hook
 */
type UploadMultiFileFn = (params: MultiFileUploadParams) => Promise<void>;

/**
 * @returns This hook returns callback to perform multiple file uploads.
 */
export function useMultiFileUploader(): UploadMultiFileFn {
  const { multiFileUploadManager } = useFileUploadContext();

  const uploadFiles = useCallback<UploadMultiFileFn>(
    (params) => multiFileUploadManager.startMultiFileUpload(params),
    [multiFileUploadManager],
  );

  return uploadFiles;
}

export type UploadedFile = {
  /** The unique id of the uploaded file */
  id: string;
  /** The url to download the file from the backend */
  downloadUrl: string;
  /** The md5 hash identifying file integrity */
  md5: string;
};

type UploadFileFnWithPromise = (
  params: SingleFileUploadParams,
) => Promise<UploadedFile>;

/**
 * @returns callback to perform the file upload via promise.
 */
export function useFileUploaderWithPromise(): UploadFileFnWithPromise {
  const { singleFileUploadManager: uploadManager } = useFileUploadContext();

  return useCallback<UploadFileFnWithPromise>(
    (params) =>
      new Promise((resolve, reject) => {
        const { onUploadCompleted, onUploadFailed, ...rest } = params;
        uploadManager.startFileUpload({
          ...rest,
          onUploadCompleted(id, downloadUrl, md5) {
            onUploadCompleted?.(id, downloadUrl, md5);
            resolve({ id, downloadUrl, md5 });
          },
          onUploadFailed(id, error) {
            onUploadFailed?.(id, error);
            reject(error);
          },
        });
      }),
    [uploadManager],
  );
}
